import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaPaymentsConfig1Service, serviceName } from '../service';
import { GetLocationCapabilitiesIO, ServiceQueries } from '../types';
import { usePaymentsConfig1Queries } from './use-payments-config1-queries';
import { UsePaymentsConfig1QueryEndpointArgs, usePaymentsConfig1Query } from './use-payments-config1-query';

/**
 * A hook that returns a query for the `GetLocationCapabilities` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetLocationCapabilitiesQuery = <E = unknown, D = GetLocationCapabilitiesIO['output']>(
  args: UsePaymentsConfig1QueryEndpointArgs<'GetLocationCapabilities', E, D>
) =>
  usePaymentsConfig1Query({
    endpointName: 'GetLocationCapabilities',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetLocationCapabilities` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetLocationCapabilitiesQueries = <E = unknown, D = GetLocationCapabilitiesIO['output']>(
  argsArr: UsePaymentsConfig1QueryEndpointArgs<'GetLocationCapabilities', E, D>[]
) =>
  usePaymentsConfig1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetLocationCapabilities',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetLocationCapabilities` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetLocationCapabilities = () => {
  const queryClient = useQueryClient();

  const fetchGetLocationCapabilities = async <E = unknown, D = GetLocationCapabilitiesIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UsePaymentsConfig1QueryEndpointArgs<'GetLocationCapabilities', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetLocationCapabilitiesIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetLocationCapabilities'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetLocationCapabilitiesIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetLocationCapabilities'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetLocationCapabilities'>({
          serviceName,
          endpointName: 'GetLocationCapabilities',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaPaymentsConfig1Service.GetLocationCapabilities(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetLocationCapabilities;
};
